import {EnvironmentProviders, makeEnvironmentProviders} from '@angular/core';
import {NavigationActionTiming, provideRouterStore, routerReducer} from '@ngrx/router-store';
import {RouterSerializer} from '@aviary/store/router/router.serializer';
import {provideState} from '@ngrx/store';
import {ROUTER_FEATURE_KEY} from '@aviary/store/router/router.types';

export function provideAviaryRouterStore(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideState(ROUTER_FEATURE_KEY, routerReducer),
    provideRouterStore({
      serializer: RouterSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation // (*) See commentary below.
    })
  ]);
}

/*
  (*) This navigationActionTiming setting prevents router data from changing before route guards finish.

  NavigationActionTiming.PostActivation seems like a reasonable setting for all of our apps.  This makes our router selectors reflect the route that
  actually activated, rather than a requested route that guards may still cancel.

  With the default NavigationActionTiming.PreActivation, the user can attempt to navigate to a new route, which dispatches REQUEST and NAVIGATION actions
  before guards run, and then either a NAVIGATED action or a CANCEL action after the guards have run. The NAVIGATION action contains the new route's URL,
  which immediately updates the router state and makes it look as though we've already navigated, regardless of whether a guard would then cancel that
  navigation or not.  A CANCEL action then re-updates the state to contain the original route, but by then it's too late -- we may have already reacted to
  the route URL change that happened before the guard could do its job.

  But with NavigationActionTiming.PostActivation, the user can attempt to navigate to a new route, which fires only the REQUEST action before guards run,
  and then NAVIGATION and NAVIGATED actions or a CANCEL action. Thus, the router state doesn't change until after the guard has had a chance to cancel the
  requested navigation.

  Hawk's EditMarcRecordComponent was the first to expose this problem, because it has a form group selector that depends on
  routerSelectors.routeNameMatches('EDIT_MARC_RECORD'). Since the NAVIGATION action was being dispatched prematurely, this selector changed from true
  to false, which caused the form group's value to be reset, which cleared all the information about which fields had been edited. (Which was a problem
  since the guard was supposed to allow the user to decide to continue editing and not lose changes.)

  It is possible that we could someday need to see the NAVIGATION action data to determine whether to go through with a route change request. Hopefully
  not. But if so, we'd have to restore this setting to PreActivation, and then we'd have to reexamine our RouterSerializer and see if we can prevent it
  from changing its calculated current route value when we've only dispatched the NAVIGATION action, but not the NAVIGATED action yet.

  References:
    - https://v16.ngrx.io/guide/router-store/actions
    - https://github.com/ngrx/platform/issues/816
    - https://ngrx.io/guide/router-store/configuration#navigation-action-timing
*/
